import React, { useRef } from 'react';
import { Box, Container, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/system';
import { Spinner } from '@chakra-ui/react';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { ReusableDrawer } from '../ReusableDrawer/ReusableDrawer';

type LayoutProps = {
    RightComponent?: React.ReactNode;
    TopComponent?: React.ReactNode;
    BottomLeftComponent?: React.ReactNode;
    BottomRightComponent?: React.ReactNode;
    centerContentLoading?: boolean;
    onCenterEndReached?: () => void;
    onRightEndReached?: () => void;
    openRightInMobile?: boolean;
    enableRightMobile?: boolean;
    onCloseRightMobile?: VoidFunction;
    CenteredBottomContainer?: React.ReactNode;
};

const offset = 600;

const ListPageLayout: React.FC<LayoutProps> = ({
    RightComponent,
    TopComponent,
    BottomLeftComponent,
    BottomRightComponent,
    centerContentLoading,
    onCenterEndReached,
    onRightEndReached,
    enableRightMobile,
    onCloseRightMobile,
    openRightInMobile,
    CenteredBottomContainer,
}) => {
    const themeOs = useTheme();
    const isMobile = useMediaQuery(themeOs.breakpoints.down('md'));
    const boxRef = useRef<any>(null);
    const rightRef = useRef<any>(null);
    const { themeStyle } = useThemeContext();

    const handleScroll = () => {
        const box = boxRef.current;
        if (box) {
            const { scrollTop, scrollHeight, clientHeight } = box;
            if (scrollTop + clientHeight >= scrollHeight - offset) {
                onCenterEndReached && onCenterEndReached();
            }
        }
    };

    const handleRightScroll = () => {
        const rightRef = boxRef.current;
        if (rightRef) {
            const { scrollTop, scrollHeight, clientHeight } = rightRef;
            if (scrollTop + clientHeight >= scrollHeight - offset) {
                onRightEndReached && onRightEndReached();
            }
        }
    };

    return (
        <Container disableGutters sx={{ mt: 2 }} maxWidth={false}>
            <Box
                display="flex"
                flexDirection={isMobile ? 'column' : 'row'}
                height="100dvh"
                // width="100%"
                gap={2}
                sx={{
                    // width: '100%',
                    marginTop: { xs: '0', sm: '20px', md: '20px', lg: 0 },
                    marginLeft: { xs: '0px', sm: '20px', lg: '20px', lgXxxl: '100px' },
                    paddingLeft: { xs: '0', md: '10px' },
                    paddingRight: { xs: '0', md: 0 },
                }}>
                {/* Right Section (Top and Bottom) */}
                <Box
                    flex={3}
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    onScroll={handleScroll}
                    ref={boxRef}
                    sx={{
                        height: '100%',
                        overflow: 'auto',
                    }}>
                    {/* Top Container */}
                    <Box
                        sx={{
                            flex: 1,
                            padding: themeOs.spacing(1),
                            ml: {
                                xs: 0,
                                sm: 3,
                            },
                        }}>
                        {centerContentLoading && (
                            <Box
                                width={'100%'}
                                position={'absolute'}
                                top={'10%'}
                                left={'50%'}
                                right={'50%'}
                                zIndex={9999}>
                                <Spinner h={10} w={10} color={theme[themeStyle].tag_white} />
                            </Box>
                        )}
                        {TopComponent}
                    </Box>

                    {/* Bottom Section */}
                    <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={2}>
                        {/* Bottom Left Container */}
                        <Box
                            flex={1}
                            sx={{
                                padding: themeOs.spacing(1),
                            }}>
                            {BottomLeftComponent}
                        </Box>

                        {/* Bottom Right Container */}
                        <Box
                            flex={1}
                            sx={{
                                padding: themeOs.spacing(1),
                            }}>
                            {BottomRightComponent}
                        </Box>
                    </Box>

                    {/* Bottom Section */}
                    <Box display="flex" flexDirection={'column'} gap={2}>
                        {/* Bottom Right Container */}
                        <Box
                            flex={1}
                            sx={{
                                padding: themeOs.spacing(2),
                                m: 2,
                            }}>
                            {CenteredBottomContainer}
                        </Box>
                    </Box>
                </Box>
                {/** Bottom Container */}
                {/* Left Container - Hidden on Mobile */}
                {!isMobile && (
                    <Box
                        ref={rightRef}
                        onScroll={handleRightScroll}
                        sx={{
                            height: '100%',
                            overflowY: 'auto', // Enables independent scrolling
                            padding: themeOs.spacing(2),
                            mr: 1,
                        }}>
                        {RightComponent}
                    </Box>
                )}

                {enableRightMobile && isMobile && (
                    <ReusableDrawer
                        open={openRightInMobile!}
                        onClose={onCloseRightMobile!}
                        anchor="right"
                        content={
                            <Container sx={{ height: '100vh', overflowY: 'auto', padding: 2 }} maxWidth={false}>
                                {RightComponent}
                            </Container>
                        }
                    />
                )}
            </Box>
        </Container>
    );
};

export default ListPageLayout;
